import {Component, OnInit} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {finalize} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {BlogApiService} from '../api/blog-api.service';
import {ProfileImageService} from '../api/profile-image.service';
import {Blog, BlogComment} from '../models/blog.model';
import {SpinnerService} from '../shared/services/spinner.service';

/** A single blog post with its like button, comments and (for authors/moderators) edit and hide actions. */
@Component({
    selector: 'app-blog',
    templateUrl: './blog.component.html',
    styleUrls: ['./blog.component.css']
})
export class BlogComponent implements OnInit {
    blog: Blog | null = null;
    // The API sanitizes post bodies on save (script/handlers stripped), so it is safe to keep the editor's
    // inline styles (alignment, colours) that Angular's own innerHTML sanitizer would otherwise remove.
    body: SafeHtml = '';
    comments: BlogComment[] = [];
    newComment = '';
    // commenter photos by member id; a commenter without an entry shows initials
    avatars: Record<string, string> = {};
    loggedIn = false;
    notFound = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private blogApi: BlogApiService,
        private profileImages: ProfileImageService,
        private spinner: SpinnerService,
        private toastr: ToastrService,
        private sanitizer: DomSanitizer
    ) {
    }

    ngOnInit(): void {
        this.loggedIn = sessionStorage.getItem('username') !== null;
        this.route.paramMap.subscribe(params => {
            const id = params.get('id');
            if (id) this.load(id);
        });
    }

    private load(id: string): void {
        this.spinner.show();
        this.blogApi.get(id).pipe(finalize(() => this.spinner.hide())).subscribe({
            next: blog => {
                this.blog = blog;
                this.body = this.sanitizer.bypassSecurityTrustHtml(blog.contentHtml || '');
                this.loadComments(id);
            },
            error: err => {
                console.error('Failed to load blog', err);
                this.notFound = true;
            }
        });
    }

    private loadComments(id: string): void {
        this.blogApi.getComments(id).subscribe({
            next: comments => {
                this.comments = comments;
                comments.forEach(c => this.loadAvatar(c.userId, c.userPhotoUri));
            },
            error: err => console.error('Failed to load comments', err)
        });
    }

    toggleLike(): void {
        if (!this.blog) return;
        if (!this.loggedIn) {
            this.router.navigate(['/login']);
            return;
        }
        const blog = this.blog;
        const request = blog.likedByMe ? this.blogApi.unlike(blog.blogId) : this.blogApi.like(blog.blogId);
        request.subscribe({
            next: () => {
                blog.likeCount += blog.likedByMe ? -1 : 1;
                blog.likedByMe = !blog.likedByMe;
            },
            error: err => {
                console.error('Like failed', err);
                this.toastr.error('Could not update your like');
            }
        });
    }

    postComment(): void {
        const text = this.newComment.trim();
        if (!this.blog || !text) return;
        this.spinner.show();
        this.blogApi.addComment(this.blog.blogId, text).pipe(finalize(() => this.spinner.hide())).subscribe({
            next: comment => {
                this.comments = [...this.comments, comment];
                this.loadAvatar(comment.userId, comment.userPhotoUri);
                this.blog!.commentCount++;
                this.newComment = '';
            },
            error: err => {
                console.error('Comment failed', err);
                this.toastr.error(err?.error?.message || 'Could not post your comment');
            }
        });
    }

    deleteComment(comment: BlogComment): void {
        if (!this.blog || !confirm('Delete this comment?')) return;
        this.blogApi.deleteComment(this.blog.blogId, comment.commentId).subscribe({
            next: () => {
                this.comments = this.comments.filter(c => c.commentId !== comment.commentId);
                this.blog!.commentCount--;
            },
            error: err => {
                console.error('Delete comment failed', err);
                this.toastr.error('Could not delete the comment');
            }
        });
    }

    toggleHidden(): void {
        if (!this.blog) return;
        const hiding = this.blog.status === 'PUBLISHED';
        const reason = hiding ? prompt('Reason for hiding this post (optional):') : null;
        if (hiding && reason === null) return;   // cancelled
        const request = hiding ? this.blogApi.hide(this.blog.blogId, reason || undefined) : this.blogApi.unhide(this.blog.blogId);
        request.subscribe({
            next: updated => {
                this.blog = {...this.blog!, status: updated.status, hiddenReason: updated.hiddenReason};
                this.toastr.success(hiding ? 'Post hidden' : 'Post is visible again');
            },
            error: err => {
                console.error('Moderation failed', err);
                this.toastr.error('Could not update the post');
            }
        });
    }

    deleteBlog(): void {
        if (!this.blog || !confirm('Delete this post permanently, with its comments and likes?')) return;
        this.blogApi.delete(this.blog.blogId).subscribe({
            next: () => {
                this.toastr.success('Post deleted');
                this.router.navigate(['/blogs']);
            },
            error: err => {
                console.error('Delete failed', err);
                this.toastr.error('Could not delete the post');
            }
        });
    }

    private loadAvatar(userId: string, photo?: string): void {
        if (this.avatars[userId]) return;
        this.profileImages.photo(userId, photo).subscribe(url => {
            if (url) this.avatars[userId] = url;
        });
    }

    initials(name: string): string {
        return (name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    }
}
